import React, { useState, useMemo } from 'react';

import dayjs from 'dayjs';
import { format, addDays, parse, startOfWeek, endOfWeek, isSameDay, set } from 'date-fns';

import {
    Typography,
    Grid,
    Box,
    IconButton,
    ListItem,
    List,
    ListItemButton,
    Popover,
    CircularProgress,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InfoOutlined from '@mui/icons-material/InfoOutlined';

import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { useOperations } from '../hooks/useOperations';
import { FieldControl, StatusChip, DialogBox } from '../../../shared/components';
import { EventHistory } from './EventHistory';
import { useMarkAsCompleted } from '../hooks/useMarkAsCompleted';

interface OperationEventDetail {
    id: string;
    recurring_plan_key: string;
    recurring_plan_display_text: string;
    helper_text: string;
    due_date: string;
    completed_date: string | null;
    status: 'Completed' | 'Overdue' | 'Pending';
    status_display_text: string;
};

export const CalendarView: React.FC<{ projectId: string | undefined; }> = ({ projectId }) => {

    const [selectedEvent, setSelectedEvent] = useState<{
        status: boolean;
        event: OperationEventDetail | null;
    }>({
        status: false,
        event: null,
    });

    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [openNoOperations, setOpenNoOperations] = useState(false);

    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

    // Calculate start and end dates for the current week
    const { startDate, endDate } = useMemo(() => {
        const currentDate = selectedDate.toDate();
        const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 }); // 0 = Sunday, 1 = Monday
        const weekEnd = endOfWeek(currentDate, { weekStartsOn: 0 });
        return {
            startDate: format(weekStart, 'dd-MM-yyyy'),
            endDate: format(weekEnd, 'dd-MM-yyyy'),
        };
    }, [selectedDate]);

    const { operations, loading: loadingOperations, error: errorOperations, refetch } = useOperations({
        projectId,
        startDate,
        endDate,
    });

    const today = format(new Date(), 'dd-MM-yyyy');

    // Generate all 7 days of the week
    const weekDays = useMemo(() => {
        const currentDate = selectedDate.toDate();
        const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 });

        return Array.from({ length: 7 }, (_, i) => {
            const date = addDays(weekStart, i);
            const dateStr = format(date, 'dd-MM-yyyy');
            const dayData = operations.find((op: any) => op.date === dateStr);

            return {
                date: dateStr,
                events: dayData?.events || [],
            };
        });
    }, [selectedDate, operations]);

    const handlePreviousWeek = () => {
        const newDate = addDays(selectedDate.toDate(), -7);
        setSelectedDate(dayjs(newDate));
    };

    const handleNextWeek = () => {
        const newDate = addDays(selectedDate.toDate(), 7);
        setSelectedDate(dayjs(newDate));
    };

    const handlePopoverOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
        setOpenNoOperations(true);
    };

    // Get current week number
    const currentWeek = Math.ceil(selectedDate.date() / 7);

    const {
        isOpen,
        setIsOpen,
        error,
        setError,
        loading,
        completeNotes,
        setCompleteNotes,
        handleMarkAsCompleted
    } = useMarkAsCompleted({ projectId, refetch });

    if (loadingOperations) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
            >
                <CircularProgress />
            </Box>
        )
    };

    if (errorOperations) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
            >
                <Typography variant="h6" color="error">
                    {errorOperations || "Error loading data."}
                </Typography>
            </Box>
        )
    };

    return (
        <Box p={3}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            views={['month', 'year']}
                            value={selectedDate}
                            format="MMM YYYY"
                            onChange={newValue => newValue && setSelectedDate(newValue)}
                            label="Select Month"
                            slotProps={{
                                textField: {
                                    inputProps: {
                                        readOnly: true,
                                    },
                                    size: 'small',
                                    sx: {
                                        maxWidth: '150px',
                                    },
                                },
                            }}
                        />
                    </LocalizationProvider>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton onClick={handlePreviousWeek}>
                        <ChevronLeftIcon />
                    </IconButton>
                    <Typography>Week {currentWeek}</Typography>
                    <IconButton onClick={handleNextWeek}>
                        <ChevronRightIcon />
                    </IconButton>
                </Box>
            </Box>
            <Box sx={{ overflow: 'hidden', height: '100%' }}>
                {/* Calendar Header */}
                <Grid container>
                    {weekDays.map((day: { date: any; }, index: React.Key | null | undefined) => (
                        <Grid
                            key={index}
                            size={12 / 7}
                            border={1}
                            borderColor={'divider'}
                        >
                            <Box
                                sx={{
                                    textAlign: 'center',
                                    flexDirection: 'column',
                                    display: 'flex',
                                    alignItems: 'center',
                                    py: 1.5,
                                }}
                            >
                                <Typography
                                    color="textSecondary"
                                    variant="caption"
                                >
                                    {format(parse(day.date, 'dd-MM-yyyy', new Date()), 'EEE').toUpperCase()}
                                </Typography>
                                <Typography
                                    sx={{
                                        mt: 0.5,
                                        width: 32,
                                        height: 32,
                                        lineHeight: '32px',
                                        margin: '0 auto',
                                        borderRadius: '50%',
                                        ...(isSameDay(parse(day.date, 'dd-MM-yyyy', new Date()), parse(today, 'dd-MM-yyyy', new Date())) && {
                                            bgcolor: 'primary.main',
                                            color: '#fff',
                                        }),
                                    }}
                                >
                                    {format(parse(day.date, 'dd-MM-yyyy', new Date()), 'd')}
                                </Typography>
                                <Typography
                                    color="textSecondary"
                                    variant="caption"
                                >
                                    {format(parse(day.date, 'dd-MM-yyyy', new Date()), 'MMM yyyy')}
                                </Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>

                {/* Calendar Body */}
                <Grid
                    container
                    sx={{ height: '100%' }}
                >
                    {weekDays.map((day: { events: OperationEventDetail[]; }, dayIndex: React.Key | null | undefined) => (
                        <Grid
                            key={dayIndex}
                            size={12 / 7}
                            border={1}
                            borderColor={'divider'}
                        >
                            <List disablePadding>
                                {day.events.map((operation: OperationEventDetail, opIndex: React.Key | null | undefined) => (
                                    <ListItem
                                        key={opIndex}
                                        disablePadding
                                        sx={{
                                            px: 1,
                                            my: 1,
                                        }}
                                    >
                                        <ListItemButton
                                            onClick={() => setSelectedEvent({
                                                status: true,
                                                event: operation,
                                            })}
                                            sx={{
                                                border: 1,
                                                borderColor: 'divider',
                                                bgcolor: 'action.hover',
                                                p: 1.5,
                                                borderRadius: '12px',
                                                lineHeight: '1',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'flex-start',
                                                gap: 1,
                                            }}
                                        >
                                            <Typography variant="caption">
                                                {operation.recurring_plan_display_text}
                                            </Typography>
                                            <StatusChip
                                                label={operation.status_display_text}
                                                color={operation.status === 'Overdue' ? 'red' : operation.status === 'Completed' ? 'green' : 'default'}
                                                variant='filled'
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                            </List>
                            {day.events.length === 0 && (
                                <Box
                                    sx={{
                                        textAlign: 'center',
                                        py: 1.5,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <IconButton onClick={handlePopoverOpen}>
                                        <InfoOutlined
                                            sx={{
                                                color: '#333',
                                                fontSize: '14px',
                                            }}
                                        />
                                    </IconButton>
                                    <Typography
                                        color="textSecondary"
                                        variant="caption"
                                        sx={{
                                            mt: 1,
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            px: 2,
                                        }}
                                    >
                                        No operations found for this day
                                    </Typography>
                                    <Popover
                                        open={openNoOperations}
                                        anchorEl={anchorEl}
                                        onClose={() => setOpenNoOperations(false)}
                                        anchorOrigin={{
                                            vertical: 'bottom',
                                            horizontal: 'left',
                                        }}
                                        transformOrigin={{
                                            vertical: 'top',
                                            horizontal: 'left',
                                        }}
                                        PaperProps={{
                                            sx: {
                                                p: 2,
                                                maxWidth: '400px',
                                                boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
                                            },
                                        }}
                                    >
                                        <Typography sx={{ fontSize: '14px' }}>
                                            Please check Project Start Date and End Date
                                        </Typography>
                                    </Popover>
                                </Box>
                            )}
                        </Grid>
                    ))}
                </Grid>
            </Box>

            <DialogBox
                showModal={selectedEvent.status}
                title={'Edit Operation'}
                subTitle={''}
                onModalClose={() => setSelectedEvent({ status: false, event: null })}
                actions={[
                    {
                        label: 'Close',
                        variant: 'text',
                        handleClick: () => setSelectedEvent({ status: false, event: null }),
                    },
                    {
                        label: 'Mark as Completed',
                        disabled: selectedEvent?.event?.status === 'Completed',
                        variant: 'contained',
                        handleClick: () => {
                            setSelectedEvent({ ...selectedEvent, status: false });
                            setIsOpen(true);
                        },
                    }
                ]}
                children={
                    <EventHistory
                        projectId={projectId}
                        activityId={selectedEvent.event?.id}
                        dueDate={selectedEvent.event?.due_date}
                    />
                }
            />

            <DialogBox
                showModal={isOpen}
                title={'Mark Operation as Completed'}
                subTitle={''}
                onModalClose={() => {
                    setIsOpen(false);
                    setCompleteNotes("");
                    setSelectedEvent({ ...selectedEvent, status: true });
                }}
                actions={[
                    {
                        label: 'Close',
                        variant: 'text',
                        handleClick: () => {
                            setIsOpen(false);
                            setCompleteNotes("");
                            setSelectedEvent({ ...selectedEvent, status: true });
                        },
                    },
                    {
                        label: 'Mark as Completed',
                        disabled: selectedEvent?.event?.status === 'Completed',
                        variant: 'contained',
                        handleClick: async () => {
                            if (selectedEvent.event) {
                                await handleMarkAsCompleted(selectedEvent.event.id, selectedEvent.event.due_date);
                            }
                        },
                    }
                ]}
                children={
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <Typography variant="body2">
                            Please provide completion notes for this operation:
                        </Typography>
                        <FieldControl
                            keyName={'notes'}
                            label={'Notes'}
                            value={completeNotes}
                            multiline
                            rows={4}
                            error={!!error}
                            helperText={error || ''}
                            variant={'input'}
                            onChange={(_key: string, value: string) => setCompleteNotes(value)}
                        />
                    </Box>
                }
            />

        </Box>
    );

};